/**
 * Living SOPs Analytics Dashboard (Phase 23.5.3)
 *
 * Manager+ dashboard surfacing the KPIs from the Phase 23 plan §6 table. The
 * hero metric is the Delta Signal-to-Noise Ratio — approved performance-delta
 * proposals divided by decided (approved + rejected) delta proposals. Goal is
 * >40% at T+3mo after Phase 23.5 ships.
 *
 * Layout mirrors `Ring2EffectivenessPage.tsx`: header + date-range toggle,
 * hero tile, grid of summary tiles, then breakdown tables. No chart library
 * dependency — the page relies on card + table primitives.
 *
 * Consumes `/api/analytics/living-sops`.
 */

import { useState } from 'react';
import { Activity, FileText, Loader2, TrendingUp } from 'lucide-react';
import {
  useLivingSopsAnalytics,
  type LivingSopsDateRange,
  type LivingSopsOutcomeTag,
  type LivingSopsOutcomeTagRow,
  type LivingSopsApprovalRate,
  type LivingSopsProposalSource,
  type LivingSopsProposalState,
} from '../../hooks/useLivingSopsAnalytics';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { cn } from '../../lib/utils';

// ============================================
// CONSTANTS
// ============================================

const PRESETS: { value: LivingSopsDateRange; label: string }[] = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
];

const SOURCE_LABEL: Record<LivingSopsProposalSource, string> = {
  struggle_signal: 'Struggle signal',
  performance_delta: 'Performance delta',
  coach_mining: 'Coach mining',
  manual: 'Manual (trainer)',
};

const STATE_LABEL: Record<LivingSopsProposalState, string> = {
  draft: 'Draft',
  pending_review: 'Pending review',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected',
  superseded: 'Superseded',
  expired: 'Expired',
};

const SOURCE_ORDER: LivingSopsProposalSource[] = [
  'performance_delta',
  'struggle_signal',
  'coach_mining',
  'manual',
];

const STATE_ORDER: LivingSopsProposalState[] = [
  'draft',
  'pending_review',
  'approved',
  'published',
  'rejected',
  'superseded',
  'expired',
];

// SNR color thresholds per spec: emerald if >40%, amber 20-40%, red <20%.
function snrTone(snr: number): {
  border: string;
  text: string;
  bg: string;
  label: string;
} {
  if (snr >= 40) {
    return {
      border: 'border-emerald-300',
      text: 'text-emerald-700',
      bg: 'bg-emerald-50',
      label: 'Above target',
    };
  }
  if (snr >= 20) {
    return {
      border: 'border-amber-300',
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      label: 'Developing',
    };
  }
  return {
    border: 'border-red-300',
    text: 'text-red-700',
    bg: 'bg-red-50',
    label: 'Below target',
  };
}

// Outcome-tag badge palette — emerald (win), amber (neutral), red (regress),
// slate (data gap).
function outcomeBadge(tag: LivingSopsOutcomeTag): string {
  switch (tag) {
    case 'improved':
      return 'bg-emerald-100 text-emerald-700';
    case 'regressed':
      return 'bg-red-100 text-red-700';
    case 'unchanged':
      return 'bg-amber-100 text-amber-700';
    case 'insufficient_data':
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

const OUTCOME_LABEL: Record<LivingSopsOutcomeTag, string> = {
  improved: 'Improved',
  unchanged: 'Unchanged',
  regressed: 'Regressed',
  insufficient_data: 'Insufficient data',
};

function formatMs(ms: number): string {
  if (!isFinite(ms) || ms <= 0) return '--';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} h`;
}

function formatScore(value: number | null): string {
  if (value == null || !isFinite(value)) return '--';
  return value.toFixed(1);
}

function formatDelta(value: number | null): string {
  if (value == null || !isFinite(value)) return '--';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

// ============================================
// PAGE
// ============================================

export function LivingSopsAnalyticsPage() {
  useDocumentTitle('Living SOPs Analytics');
  const [preset, setPreset] = useState<LivingSopsDateRange>('30d');
  const { data, isLoading, isError } = useLivingSopsAnalytics(preset);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Failed to load Living SOPs analytics data.
      </div>
    );
  }

  const snrInfo = snrTone(data.deltaSignalToNoiseRatio.snr);
  const hasAnyProposals = data.proposalVolumes.total > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-guild-green/10 text-guild-green">
            <FileText size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Living SOPs Analytics
            </h1>
            <p className="text-sm text-gray-500">
              Proposal volume, approval rates, delta signal-to-noise, cascade
              latency
            </p>
          </div>
        </div>

        {/* Date-range toggle */}
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPreset(p.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200',
                preset === p.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty-state card */}
      {!hasAnyProposals && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <Activity size={32} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-sm font-medium text-gray-700">
            No proposals in this period
          </h3>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            The Living SOPs pipeline will publish proposals as Phase 23.3 and
            Phase 23.5 detectors fire. Try widening the date range.
          </p>
        </div>
      )}

      {hasAnyProposals && (
        <>
          {/* Hero: Delta SNR tile */}
          <div
            className={cn(
              'rounded-xl border p-6',
              snrInfo.border,
              snrInfo.bg
            )}
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className={snrInfo.text} />
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Delta Signal-to-Noise Ratio
                  </p>
                </div>
                <p
                  className={cn(
                    'mt-2 text-5xl font-bold tabular-nums',
                    snrInfo.text
                  )}
                >
                  {data.deltaSignalToNoiseRatio.snr.toFixed(1)}%
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  {snrInfo.label} — goal is &gt;40% at T+3mo.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Approved: {data.deltaSignalToNoiseRatio.approved} · Rejected:{' '}
                  {data.deltaSignalToNoiseRatio.rejected}
                </p>
              </div>
              <div className="max-w-xs text-xs text-gray-600">
                <p className="font-medium text-gray-700">What is SNR?</p>
                <p className="mt-1">
                  The share of performance-delta proposals the trainer
                  approves. A high SNR means the detector is surfacing real
                  improvements. A low SNR means it is surfacing noise — tune
                  the detector or raise the delta threshold.
                </p>
              </div>
            </div>
          </div>

          {/* Summary tiles — total proposals, overall approval, cascade latency */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <SummaryTile
              label="Total proposals"
              value={data.proposalVolumes.total.toLocaleString()}
              sublabel={`In last ${preset.replace('d', ' days')}`}
            />
            <SummaryTile
              label="Overall approval"
              value={`${data.approvalRates.overall.rate.toFixed(1)}%`}
              sublabel={`${data.approvalRates.overall.approved} approved · ${data.approvalRates.overall.rejected} rejected`}
            />
            <SummaryTile
              label="Cascade p50"
              value={formatMs(data.cascadeLatency.p50Ms)}
              sublabel="Approve -> published (median)"
            />
            <SummaryTile
              label="Cascade p90"
              value={formatMs(data.cascadeLatency.p90Ms)}
              sublabel={`${data.cascadeLatency.sampleSize} versions in window`}
            />
          </div>

          {/* Proposal volume matrix */}
          <Card
            title="Proposal volumes"
            subtitle="Counts by source and by state in the selected window"
          >
            <VolumeMatrix
              bySource={data.proposalVolumes.bySource}
              byState={data.proposalVolumes.byState}
              total={data.proposalVolumes.total}
            />
          </Card>

          {/* Approval rate by source */}
          <Card
            title="Approval rate by source"
            subtitle="Share of decided proposals (approved + rejected) that were approved"
          >
            <ApprovalBySourceTable rows={data.approvalRates.bySource} />
          </Card>

          {/* Delta outcome tags */}
          <Card
            title="Delta outcome tags"
            subtitle="Post-hoc comparison of avg section score in the 30d before vs 30d after publish. Requires 5+ assessments on each side and 30d of post-publish data."
          >
            {data.deltaOutcomeTags.length === 0 ? (
              <EmptyChart message="No approved delta proposals have 30 days of post-publish assessment data yet." />
            ) : (
              <OutcomeTagsTable rows={data.deltaOutcomeTags} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-5 py-3">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">
        {value}
      </p>
      {sublabel && <p className="mt-0.5 text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
}

function EmptyChart({ message }: { message?: string }) {
  return (
    <div className="flex h-24 items-center justify-center text-xs text-gray-400">
      {message ?? 'No data in this window.'}
    </div>
  );
}

function VolumeMatrix({
  bySource,
  byState,
  total,
}: {
  bySource: Record<LivingSopsProposalSource, number>;
  byState: Record<LivingSopsProposalState, number>;
  total: number;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          By source
        </h3>
        <div className="mt-3 space-y-2">
          {SOURCE_ORDER.map((source) => {
            const value = bySource[source] ?? 0;
            const pct = total === 0 ? 0 : (value / total) * 100;
            return (
              <VolumeBar
                key={source}
                label={SOURCE_LABEL[source]}
                value={value}
                pct={pct}
                tone={source === 'performance_delta' ? 'primary' : 'muted'}
              />
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          By state
        </h3>
        <div className="mt-3 space-y-2">
          {STATE_ORDER.map((state) => {
            const value = byState[state] ?? 0;
            const pct = total === 0 ? 0 : (value / total) * 100;
            return (
              <VolumeBar
                key={state}
                label={STATE_LABEL[state]}
                value={value}
                pct={pct}
                tone={state === 'published' ? 'primary' : 'muted'}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function VolumeBar({
  label,
  value,
  pct,
  tone,
}: {
  label: string;
  value: number;
  pct: number;
  tone: 'primary' | 'muted';
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-gray-700">{label}</span>
        <span className="tabular-nums text-gray-500">
          {value} ({pct.toFixed(0)}%)
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100">
        <div
          className={cn(
            'h-full rounded transition-all',
            tone === 'primary' ? 'bg-guild-green' : 'bg-gray-400'
          )}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}

function ApprovalBySourceTable({
  rows,
}: {
  rows: Record<LivingSopsProposalSource, LivingSopsApprovalRate>;
}) {
  const entries = SOURCE_ORDER.map((source) => ({
    source,
    ...(rows[source] ?? { approved: 0, rejected: 0, rate: 0 }),
  }));

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2 pr-4 text-left font-medium">Source</th>
            <th className="py-2 pr-4 text-right font-medium">Approved</th>
            <th className="py-2 pr-4 text-right font-medium">Rejected</th>
            <th className="py-2 pr-4 text-right font-medium">Decisions</th>
            <th className="py-2 pr-4 text-right font-medium">Rate</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map((row) => {
            const decisions = row.approved + row.rejected;
            return (
              <tr
                key={row.source}
                className={
                  row.source === 'performance_delta' ? 'bg-guild-green/5' : ''
                }
              >
                <td className="py-2 pr-4 font-medium text-gray-900">
                  {SOURCE_LABEL[row.source]}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {row.approved}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {row.rejected}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums text-gray-500">
                  {decisions}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                  {decisions === 0 ? '--' : `${row.rate.toFixed(1)}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OutcomeTagsTable({ rows }: { rows: LivingSopsOutcomeTagRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2 pr-4 text-left font-medium">Section</th>
            <th className="py-2 pr-4 text-left font-medium">Published</th>
            <th className="py-2 pr-4 text-right font-medium">Before</th>
            <th className="py-2 pr-4 text-right font-medium">After</th>
            <th className="py-2 pr-4 text-right font-medium">Delta</th>
            <th className="py-2 text-left font-medium">Outcome</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.proposalId}>
              <td className="py-2 pr-4 font-medium text-gray-900">
                {row.sectionTitle ?? '--'}
              </td>
              <td className="py-2 pr-4 text-gray-600">
                {formatDate(row.publishedAt)}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {formatScore(row.beforeScore)}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {formatScore(row.afterScore)}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                {formatDelta(row.delta)}
              </td>
              <td className="py-2">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    outcomeBadge(row.tag)
                  )}
                >
                  {OUTCOME_LABEL[row.tag]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default LivingSopsAnalyticsPage;
